import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Form, Input, Radio } from 'antd'
import {
  businessBatchCreateSchema,
  type BusinessBatchCreateResult,
  type BusinessType
} from '@super-mega/server/contracts/business'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'

type BatchFormValues = { type: BusinessType; names: string }

function parseNames(value: string) {
  return value
    .split(/\r\n|\n|\r/)
    .map((name) => name.trim())
    .filter(Boolean)
}

export function BusinessBatchCreateModal({
  onClose,
  onSaved
}: {
  onClose: () => void
  onSaved: () => Promise<unknown>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<BatchFormValues>()
  const [result, setResult] = useState<BusinessBatchCreateResult>()
  const batchCreate = useMutation(trpc.business.batchCreate.mutationOptions())

  return (
    <ModalForm<BatchFormValues>
      name="business-batch-create"
      form={form}
      initialValues={{ type: 'lease', names: '' }}
      layout="vertical"
      modalProps={{
        destroyOnHidden: true,
        mask: { closable: false },
        closable: !batchCreate.isPending,
        keyboard: !batchCreate.isPending,
        onCancel: onClose
      }}
      onFinish={async (values) => {
        try {
          const saved = await batchCreate.mutateAsync({ type: values.type, names: parseNames(values.names) })
          setResult(saved)
          form.setFieldValue('names', saved.failures.map((failure) => failure.name).join('\n'))
          if (saved.successCount > 0) {
            await onSaved()
          }
        } catch (error) {
          void message.error(getErrorMessage(error, '批量添加失败'))
        }
        return false
      }}
      open
      scrollToFirstError
      submitter={{
        resetButtonProps: { disabled: batchCreate.isPending, onClick: onClose },
        searchConfig: { resetText: result ? '关闭' : '取消', submitText: '保存' },
        submitButtonProps: { loading: batchCreate.isPending }
      }}
      title="批量添加业务"
      width="40rem"
    >
      {result ? (
        <Alert
          className="mb-4"
          showIcon
          type={result.failureCount ? 'warning' : 'success'}
          title={`成功 ${result.successCount} 个，失败 ${result.failureCount} 个`}
          description={
            result.failureCount ? (
              <div>
                <p>失败的业务已保留在下方，可修改后重新保存：</p>
                <ul className="m-0 list-disc pl-5">
                  {result.failures.map((failure, index) => (
                    <li className="break-all" key={`${index}-${failure.name}`}>
                      {failure.name}：{failure.reason}
                    </li>
                  ))}
                </ul>
              </div>
            ) : undefined
          }
        />
      ) : null}
      <Form.Item label="业务类型" name="type" rules={[{ required: true, message: '请选择业务类型' }]}>
        <Radio.Group
          disabled={batchCreate.isPending}
          optionType="button"
          options={[
            { label: '租赁业务', value: 'lease' },
            { label: '增值业务', value: 'valueAdded' }
          ]}
        />
      </Form.Item>
      <Form.Item
        extra="一行一个业务名称，最多 100 个；空行自动忽略，颜色随机分配。同名业务会单独报错，其余正常创建。"
        label="业务名称"
        name="names"
        rules={[
          {
            required: true,
            validator: async (_, value: string | undefined) => {
              const parsed = businessBatchCreateSchema.shape.names.safeParse(parseNames(value ?? ''))
              if (!parsed.success) {
                throw new Error(parsed.error.issues[0]?.message)
              }
            }
          }
        ]}
      >
        <Input.TextArea disabled={batchCreate.isPending} placeholder={'设备租赁\n场地租赁\n会议室租赁'} rows={8} />
      </Form.Item>
    </ModalForm>
  )
}
